import type { FilterOperator, FilterState } from '../types';

const OPERATOR_SUFFIX = '_op';
const FROM_SUFFIX = '_from';
const TO_SUFFIX = '_to';
const MIN_SUFFIX = '_min';
const MAX_SUFFIX = '_max';

export type FilterAttributeUrlKind =
  | 'multi-select'
  | 'text-search'
  | 'date-range'
  | 'numeric-range'
  /**
   * Combined relative-preset + absolute-range time filter.
   *
   * Two URL shapes:
   *  - Preset:   `?<id>=last_2h` (single value, non-ISO slug)
   *  - Absolute: `?<id>_from=<iso>&<id>_to=<iso>` (two ISO values)
   *
   * Encoded `values` array follows the same disambiguation:
   *  - `[<preset-id>]` (length 1, non-ISO) → preset
   *  - `[<from>, <to>]` (length 2, ISO or empty) → absolute
   *
   * The `episode-detail` use of this kind also accepts legacy
   * `?dates_from=...&dates_to=...` keys on decode (back-compat for saved
   * views authored before the rename). See `LEGACY_TIME_RANGE_ALIASES`.
   */
  | 'time-range';

/**
 * Per-attribute legacy alias map. When decoding a `time-range` attribute,
 * if neither `<id>` nor `<id>_from` / `<id>_to` is present we fall back
 * to the aliased prefix's `_from` / `_to` keys.
 *
 * This is intentionally narrow — we only carry compat for known renames.
 */
const LEGACY_TIME_RANGE_ALIASES: Record<string, string> = {
  created: 'dates',
};

export type FilterAttributeUrlKinds = Partial<
  Record<string, FilterAttributeUrlKind>
>;

function isValidOperator(value: string | null | undefined): value is FilterOperator {
  return value === 'is' || value === 'isNot';
}

function kindFor(
  id: string,
  kinds: FilterAttributeUrlKinds | undefined,
): FilterAttributeUrlKind {
  return kinds?.[id] ?? 'multi-select';
}

function clearManagedKeys(
  params: URLSearchParams,
  id: string,
  kind: FilterAttributeUrlKind,
): void {
  params.delete(id);
  params.delete(`${id}${OPERATOR_SUFFIX}`);
  if (kind === 'date-range' || kind === 'time-range') {
    params.delete(`${id}${FROM_SUFFIX}`);
    params.delete(`${id}${TO_SUFFIX}`);
  }
  if (kind === 'numeric-range') {
    params.delete(`${id}${MIN_SUFFIX}`);
    params.delete(`${id}${MAX_SUFFIX}`);
  }
  if (kind === 'time-range') {
    const legacy = LEGACY_TIME_RANGE_ALIASES[id];
    if (legacy) {
      params.delete(legacy);
      params.delete(`${legacy}${FROM_SUFFIX}`);
      params.delete(`${legacy}${TO_SUFFIX}`);
      params.delete(`${legacy}${OPERATOR_SUFFIX}`);
    }
  }
}

/**
 * Write `state.filters` into `params`, scoped to the given attribute ids.
 *
 * URL shape (chosen for readability + history-friendly diffs):
 *   ?status=running&status=completed&status_op=isNot&tag=favorite
 *
 * The operator suffix is omitted when the default operator (`is`) is used.
 * Any pre-existing values for the same keys are removed first so the call
 * is idempotent and safe to repeat with a fresh state.
 *
 * **text-search** uses a single query value: `search=needle` (no repeats).
 *
 * **date-range** uses `id_from` and `id_to` (YYYY-MM-DD or ISO substrings).
 *
 * **numeric-range** uses `id_min` and `id_max` (number strings; empty omitted).
 */
export function encodeFilterStateToParams(
  state: FilterState,
  params: URLSearchParams,
  allowedIds: readonly string[],
  kinds?: FilterAttributeUrlKinds,
): void {
  const allowed = new Set(allowedIds);
  for (const id of allowed) {
    clearManagedKeys(params, id, kindFor(id, kinds));
  }
  for (const f of state.filters) {
    if (!allowed.has(f.attributeId)) {
      continue;
    }
    const k = kindFor(f.attributeId, kinds);
    if (k === 'text-search') {
      const q = f.values[0] ?? '';
      if (q.length === 0) {
        continue;
      }
      params.set(f.attributeId, q);
      if (f.operator !== 'is') {
        params.set(`${f.attributeId}${OPERATOR_SUFFIX}`, f.operator);
      }
      continue;
    }
    if (k === 'date-range') {
      const from = f.values[0]?.trim() ?? '';
      const to = f.values[1]?.trim() ?? '';
      if (from.length === 0 && to.length === 0) {
        continue;
      }
      if (from.length > 0) {
        params.set(`${f.attributeId}${FROM_SUFFIX}`, from);
      }
      if (to.length > 0) {
        params.set(`${f.attributeId}${TO_SUFFIX}`, to);
      }
      if (f.operator !== 'is') {
        params.set(`${f.attributeId}${OPERATOR_SUFFIX}`, f.operator);
      }
      continue;
    }
    if (k === 'numeric-range') {
      const minV = f.values[0]?.trim() ?? '';
      const maxV = f.values[1]?.trim() ?? '';
      if (minV.length === 0 && maxV.length === 0) {
        continue;
      }
      if (minV.length > 0) {
        params.set(`${f.attributeId}${MIN_SUFFIX}`, minV);
      }
      if (maxV.length > 0) {
        params.set(`${f.attributeId}${MAX_SUFFIX}`, maxV);
      }
      if (f.operator !== 'is') {
        params.set(`${f.attributeId}${OPERATOR_SUFFIX}`, f.operator);
      }
      continue;
    }
    if (k === 'time-range') {
      // Single-value form = preset slug (e.g. "last_2h"). Two-value form
      // = absolute ISO range. Empty -> nothing to encode.
      if (f.values.length === 1) {
        const presetId = f.values[0]?.trim() ?? '';
        if (presetId.length === 0) {
          continue;
        }
        params.set(f.attributeId, presetId);
      } else if (f.values.length >= 2) {
        const fromV = f.values[0]?.trim() ?? '';
        const toV = f.values[1]?.trim() ?? '';
        if (fromV.length === 0 && toV.length === 0) {
          continue;
        }
        if (fromV.length > 0) {
          params.set(`${f.attributeId}${FROM_SUFFIX}`, fromV);
        }
        if (toV.length > 0) {
          params.set(`${f.attributeId}${TO_SUFFIX}`, toV);
        }
      } else {
        continue;
      }
      if (f.operator !== 'is') {
        params.set(`${f.attributeId}${OPERATOR_SUFFIX}`, f.operator);
      }
      continue;
    }
    if (f.values.length === 0) {
      continue;
    }
    for (const v of f.values) {
      params.append(f.attributeId, v);
    }
    if (f.operator !== 'is') {
      params.set(`${f.attributeId}${OPERATOR_SUFFIX}`, f.operator);
    }
  }
}

export function decodeFilterStateFromParams(
  params: URLSearchParams,
  allowedIds: readonly string[],
  kinds?: FilterAttributeUrlKinds,
): FilterState {
  const filters: FilterState['filters'] = [];
  for (const id of allowedIds) {
    const k = kindFor(id, kinds);
    if (k === 'text-search') {
      const raw = params.get(id);
      if (!raw || raw.trim().length === 0) {
        continue;
      }
      const opRaw = params.get(`${id}${OPERATOR_SUFFIX}`);
      const operator: FilterOperator = isValidOperator(opRaw) ? opRaw : 'is';
      filters.push({ attributeId: id, operator, values: [raw] });
      continue;
    }
    if (k === 'date-range') {
      const fromRaw = params.get(`${id}${FROM_SUFFIX}`);
      const toRaw = params.get(`${id}${TO_SUFFIX}`);
      const from = fromRaw?.trim() ?? '';
      const to = toRaw?.trim() ?? '';
      if (from.length === 0 && to.length === 0) {
        continue;
      }
      const opRaw = params.get(`${id}${OPERATOR_SUFFIX}`);
      const operator: FilterOperator = isValidOperator(opRaw) ? opRaw : 'is';
      filters.push({ attributeId: id, operator, values: [from, to] });
      continue;
    }
    if (k === 'numeric-range') {
      const minRaw = params.get(`${id}${MIN_SUFFIX}`);
      const maxRaw = params.get(`${id}${MAX_SUFFIX}`);
      const minV = minRaw?.trim() ?? '';
      const maxV = maxRaw?.trim() ?? '';
      if (minV.length === 0 && maxV.length === 0) {
        continue;
      }
      const opRaw = params.get(`${id}${OPERATOR_SUFFIX}`);
      const operator: FilterOperator = isValidOperator(opRaw) ? opRaw : 'is';
      filters.push({ attributeId: id, operator, values: [minV, maxV] });
      continue;
    }
    if (k === 'time-range') {
      // Preset form takes precedence over absolute form.
      const presetRaw = params.get(id);
      if (presetRaw && presetRaw.trim().length > 0) {
        const opRaw = params.get(`${id}${OPERATOR_SUFFIX}`);
        const operator: FilterOperator = isValidOperator(opRaw) ? opRaw : 'is';
        filters.push({ attributeId: id, operator, values: [presetRaw.trim()] });
        continue;
      }
      let fromRaw = params.get(`${id}${FROM_SUFFIX}`);
      let toRaw = params.get(`${id}${TO_SUFFIX}`);
      let opSourceId = id;
      // Legacy alias fallback (e.g. `created` -> `dates`). Only consulted
      // when neither the canonical preset key nor the canonical from/to
      // keys are set.
      if (!fromRaw && !toRaw) {
        const legacyId = LEGACY_TIME_RANGE_ALIASES[id];
        if (legacyId) {
          const legacyPreset = params.get(legacyId);
          if (legacyPreset && legacyPreset.trim().length > 0) {
            const opRaw = params.get(`${legacyId}${OPERATOR_SUFFIX}`);
            const operator: FilterOperator = isValidOperator(opRaw) ? opRaw : 'is';
            filters.push({ attributeId: id, operator, values: [legacyPreset.trim()] });
            continue;
          }
          fromRaw = params.get(`${legacyId}${FROM_SUFFIX}`);
          toRaw = params.get(`${legacyId}${TO_SUFFIX}`);
          opSourceId = legacyId;
        }
      }
      const fromV = fromRaw?.trim() ?? '';
      const toV = toRaw?.trim() ?? '';
      if (fromV.length === 0 && toV.length === 0) {
        continue;
      }
      const opRaw = params.get(`${opSourceId}${OPERATOR_SUFFIX}`);
      const operator: FilterOperator = isValidOperator(opRaw) ? opRaw : 'is';
      filters.push({ attributeId: id, operator, values: [fromV, toV] });
      continue;
    }
    const values = Array.from(new Set(params.getAll(id).filter(Boolean)));
    if (values.length === 0) {
      continue;
    }
    const opRaw = params.get(`${id}${OPERATOR_SUFFIX}`);
    const operator: FilterOperator = isValidOperator(opRaw) ? opRaw : 'is';
    filters.push({ attributeId: id, operator, values });
  }
  return { filters };
}
